import { FunctionComponent, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	faSearch,
	faChevronDown,
	faChevronRight,
	faFilter,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { ContactLeadCompact } from "@/pages/Private/pages/Messages/components/LeadCardCompact/ContactLeadCompact";
import {
	IPersonSummary,
	ListPersonGlobalSearchInfoDTO,
} from "@/pages/Private/redux/search/search.schema";

import { hasDisplayableCompany, resolveDisplayCompany } from "./utils";
import { LinkedInPersonCard } from "./LinkedInPersonCard";
import { SingleAddRequest } from "./referralContext";
import { Loader } from "../Loader/Loader";

interface ByFiltersTabResultsProps {
	hasSearched: boolean;
	isSearching: boolean;
	contactResults: ListPersonGlobalSearchInfoDTO[];
	linkedInResults: IPersonSummary[];
	selectedIds: number[];
	selectedContactIds: number[];
	setSelectedContactIds: (ids: number[]) => void;
	onContactNavigate: (item: ListPersonGlobalSearchInfoDTO) => void;
	selectedLinkedInItems: {
		linkedinUrl: string;
		name: string;
		domain?: string;
		companyEnrichId?: number;
	}[];
	setSelectedLinkedInItems: (
		items: { linkedinUrl: string; name: string; domain?: string; companyEnrichId?: number }[]
	) => void;
	// Single-select (compose) mode hides the "Select all" bulk controls — selecting is one-at-a-time.
	singleSelect?: boolean;
	// Wrong-person referral flow: when set, every result row gets an "Add to outreach"
	// button that hands the single row up to the modal's list picker.
	onAddSingleToList?: (request: SingleAddRequest) => void;
}

export const ByFiltersTabResults: FunctionComponent<ByFiltersTabResultsProps> = ({
	hasSearched,
	isSearching,
	contactResults,
	linkedInResults,
	selectedIds,
	selectedContactIds,
	setSelectedContactIds,
	onContactNavigate,

	selectedLinkedInItems,
	setSelectedLinkedInItems,
	singleSelect = false,
	onAddSingleToList,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companyEnrich.${key}`);

	// Slim per-row action strip for the wrong-person referral flow.
	const renderAddToOutreach = (request: SingleAddRequest) =>
		onAddSingleToList && (
			<div className="flex justify-end border-t border-gray-100 px-3 py-1.5">
				<button
					className="text-xs font-semibold text-blue-action cursor-pointer hover:underline"
					onClick={() => onAddSingleToList(request)}
				>
					{t("inbox.thread.wrongPerson.addToOutreach")}
				</button>
			</div>
		);

	const [isContactsExpanded, setIsContactsExpanded] = useState(true);
	const [isLinkedInExpanded, setIsLinkedInExpanded] = useState(true);
	const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

	const selectableLinkedIn = useMemo(
		() => linkedInResults.filter(hasDisplayableCompany),
		[linkedInResults]
	);
	const isSelectedAllLinkedIn =
		selectableLinkedIn.length > 0 &&
		selectableLinkedIn.every((p) =>
			selectedLinkedInItems.some((li) => li.companyEnrichId === p.id)
		);
	const isSelectedAllContacts =
		contactResults.length > 0 &&
		contactResults.every(
			(c) => c?.prospect?.id !== undefined && selectedContactIds.includes(c?.prospect?.id)
		);

	const hasResults = contactResults.length > 0 || linkedInResults.length > 0;

	const toggleLinkedInItem = (person: IPersonSummary) => {
		const isSelected = selectedLinkedInItems.some((li) => li.companyEnrichId === person.id);

		if (isSelected) {
			setSelectedLinkedInItems(
				selectedLinkedInItems.filter((li) => li.companyEnrichId !== person.id)
			);

			return;
		}

		setSelectedLinkedInItems([
			...selectedLinkedInItems,
			{
				companyEnrichId: person.id,
				name: person.name,
				linkedinUrl: "",
				domain: resolveDisplayCompany(person)?.domain ?? "",
			},
		]);
	};

	if (!hasSearched) {
		return (
			<div className="flex-1 flex flex-col items-center pt-4 gap-2 px-6 pb-8 text-center">
				{" "}
				<div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
					<Icon className="text-gray-500 text-base" icon={faFilter} />
				</div>
				<p className="text-sm font-medium text-gray-700">Define who you're looking for</p>
				<p className="text-xs text-gray-500 leading-relaxed w-[300px]">
					Set filters on the left and run a search — we'll preview leads for free. Iterate as much
					as you want, then decide who to enrich or add to a list.
				</p>
			</div>
		);
	}

	if (isSearching) {
		return (
			<div className="flex justify-center items-center h-full">
				<Loader />{" "}
			</div>
		);
	}

	if (!hasResults) {
		return (
			<div className="flex flex-col items-center jh-full gap-2 text-center px-8">
				<div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
					<Icon className="text-gray-500 text-base" icon={faSearch} />
				</div>
				<p className="text-sm font-medium text-gray-700">{ts("noPeopleFound")}</p>
				<p className="text-xs text-gray-500">{ts("adjustFilters")}</p>
			</div>
		);
	}

	return (
		<div className="p-4 space-y-5">
			<section>
				<div className="flex items-center  mb-3 w-full justify-between">
					<button
						className="flex gap-1 items-center "
						onClick={() => setIsLinkedInExpanded((p) => !p)}
					>
						<Icon
							className="text-gray-600 text-[10px]"
							icon={isLinkedInExpanded ? faChevronDown : faChevronRight}
						/>
						<span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
							From LinkedIn
						</span>
						{selectableLinkedIn.length > 0 && (
							<span className="text-[10px] font-semibold text-gray-700 bg-gray-100 px-2 rounded-full">
								{selectableLinkedIn.length}
							</span>
						)}
					</button>
					{!singleSelect && (
						<button
							onClick={() => {
								if (isSelectedAllLinkedIn) {
									const currentIds = new Set(selectableLinkedIn.map((p) => p.id));

									setSelectedLinkedInItems(
										selectedLinkedInItems.filter((li) => !currentIds.has(li.companyEnrichId ?? -1))
									);

									return;
								}

								const existingIds = new Set(selectedLinkedInItems.map((li) => li.companyEnrichId));
								const toAdd = selectableLinkedIn
									.filter((person) => !existingIds.has(person.id))
									.map((person) => ({
										companyEnrichId: person.id,
										name: person.name,
										linkedinUrl: "",
										domain: resolveDisplayCompany(person)?.domain ?? "",
									}));

								setSelectedLinkedInItems([...selectedLinkedInItems, ...toAdd]);
							}}
							className=" w-fit whitespace-nowrap text-blue-action text-[11px] hover:cursor-pointer hover:underline"
						>
							{isSelectedAllLinkedIn ? "Deselect all" : "Select all"}
						</button>
					)}
				</div>
				{isLinkedInExpanded && (
					<>
						{selectableLinkedIn.length === 0 && (
							<p className="text-xs italic text-gray-500 py-1">{t("basics.noResults")}</p>
						)}
						<div className="flex flex-col gap-3">
							{selectableLinkedIn.map((person) => (
								<div
									key={`filter-preview-${person.id}`}
									className="rounded-md border relative border-status-info-light [&_.lead-item]:rounded-lg [&_.lead-item>div]:p-3 [&_.lead-item>div]:border-0 [&_.lead-item>div]:shadow-sm [&_.lead-item>div]:hover:shadow-md"
								>
									<LinkedInPersonCard
										person={person}
										isSelected={selectedIds.includes(person.id)}
										failedImages={failedImages}
										onImageError={(url) => setFailedImages((prev) => new Set([...prev, url]))}
										onToggle={() => toggleLinkedInItem(person)}
									/>
									{renderAddToOutreach({
										linkedInItem: {
											companyEnrichId: person.id,
											name: person.name,
											linkedinUrl: "",
											domain: resolveDisplayCompany(person)?.domain ?? "",
										},
									})}
								</div>
							))}
						</div>
					</>
				)}
			</section>

			<section>
				<div className="flex items-center  mb-3 w-full justify-between">
					<button
						className="flex gap-1 items-center "
						onClick={() => setIsContactsExpanded((p) => !p)}
					>
						<Icon
							className="text-gray-600 text-[10px]"
							icon={isContactsExpanded ? faChevronDown : faChevronRight}
						/>
						<span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
							Your contacts
						</span>
						{contactResults.length > 0 && (
							<span className="text-[10px] font-semibold text-gray-700 bg-gray-100 px-2 rounded-full">
								{contactResults.length}
							</span>
						)}
					</button>
					{!singleSelect && (
						<button
							onClick={() => {
								if (isSelectedAllContacts) {
									const currentIds = new Set(
										contactResults
											.map((c) => c.prospect?.id)
											.filter((id): id is number => id !== undefined)
									);

									setSelectedContactIds(selectedContactIds.filter((id) => !currentIds.has(id)));

									return;
								}

								const existingIds = new Set(selectedContactIds);
								const toAdd = contactResults
									.map((c) => c.prospect?.id)
									.filter((id): id is number => id !== undefined && !existingIds.has(id));

								setSelectedContactIds([...selectedContactIds, ...toAdd]);
							}}
							className=" w-fit whitespace-nowrap text-blue-action text-[11px] hover:cursor-pointer hover:underline"
						>
							{isSelectedAllContacts ? "Deselect all" : "Select all"}
						</button>
					)}
				</div>
				{isContactsExpanded && (
					<>
						{contactResults.length === 0 && (
							<p className="text-xs italic text-gray-500 py-1">{t("basics.noResults")}</p>
						)}
						<div className="space-y-3">
							{contactResults.map((item, idx) => (
								<div
									key={item.id ?? idx}
									className="rounded-md border border-status-info-light overflow-hidden"
								>
									<ContactLeadCompact
										contact={item}
										selectedProspectIds={selectedContactIds}
										setSelectedProspectIds={setSelectedContactIds}
										onOpen={() => onContactNavigate(item)}
									/>
									{item.prospect?.id !== undefined &&
										renderAddToOutreach({ contactId: item.prospect.id })}
								</div>
							))}
						</div>
					</>
				)}
			</section>
		</div>
	);
};
